import React from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useTradeTable } from "./useTradeTable";

type Trade = {
  id: string | number;
  symbol: string;
  date: string;
  type: "BUY" | "SELL";
  quantity: number;
  pnl: number | string;
  strategy_tag?: string;
};

type TradeTableProps = {
  trades: Trade[];
};

const TABLE_HEADER_CLASSES = "px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider break-words bg-white/5";
const TABLE_CELL_CLASSES = "px-6 py-4 text-sm text-slate-300 break-words border-b border-white/10";
const BADGE_PRIMARY_CLASSES = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-300 border border-emerald-400/20";

const TradeTable: React.FC<TradeTableProps> = ({ trades }) => {
  const { formatCurrency, formatDate, formatNumber } = useTradeTable();

  if (!trades || trades.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-slate-400">
          <TrendingUp className="mx-auto h-12 w-12 text-slate-500" />
          <h3 className="mt-2 text-sm font-medium text-slate-100">No trades found</h3>
          <p className="mt-1 text-sm text-slate-400">No trades match the selected date range.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-white/10">
        <thead className="bg-white/5">
          <tr>
            <th className={TABLE_HEADER_CLASSES}>Symbol</th>
            <th className={TABLE_HEADER_CLASSES}>Date</th>
            <th className={TABLE_HEADER_CLASSES}>Type</th>
            <th className={TABLE_HEADER_CLASSES}>Quantity</th>
            <th className={TABLE_HEADER_CLASSES}>P&L</th>
            <th className={TABLE_HEADER_CLASSES}>Model/Strategy</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {trades.map((trade) => (
            <tr key={trade.id} className="hover:bg-white/5">
              <td className={TABLE_CELL_CLASSES}>
                <div className="flex items-center">
                  <div className="text-sm font-medium text-slate-100">{trade.symbol}</div>
                </div>
              </td>

              <td className={TABLE_CELL_CLASSES}>
                <div className="text-sm text-slate-100">{formatDate(trade.date)}</div>
              </td>

              <td className={TABLE_CELL_CLASSES}>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    trade.type === "BUY" ? "bg-success-500/15 text-success-300" : "bg-danger-500/15 text-danger-300"
                  }`}
                >
                  {trade.type === "BUY" ? (
                    <TrendingUp className="mr-1 h-3 w-3" />
                  ) : (
                    <TrendingDown className="mr-1 h-3 w-3" />
                  )}
                  {trade.type}
                </span>
              </td>

              <td className={TABLE_CELL_CLASSES}>
                <div className="text-sm text-slate-100">{formatNumber(trade.quantity)}</div>
              </td>

              <td className={TABLE_CELL_CLASSES}>
                <div
                  className={`text-sm font-medium ${
                    parseFloat(String(trade.pnl)) >= 0 ? "text-success-400" : "text-danger-400"
                  }`}
                >
                  {formatCurrency(trade.pnl)}
                </div>
              </td>

              <td className={TABLE_CELL_CLASSES}>
                <div className="flex items-center">
                  <span className={BADGE_PRIMARY_CLASSES}>{trade.strategy_tag || "Manual Trade"}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TradeTable;
